/*
 *
 * EventTeamDetail reducer
 *
 */
import { createSlice } from '@reduxjs/toolkit';

import {
  getTeamDetailError,
  getTeamDetailSuccess,
  resetGetTeamDetail,
  getTeamDetail,
  setTeamBibNumberSuccess,
  setTeamBibNumber,
  setTeamBibNumberError,
  updateTagState,
  updateTagStateError,
  updateTagStateSuccess,
  updateStartTimeSuccess,
  updateStartTimeError,
  updateStartTime,
  updateTeamStatus,
  updateTeamStatusError,
  updateTeamStatusSuccess,
  updateDocumentStatusAct,
  assignUserAct,
  assignUserErrorAct,
  assignUserSuccessAct,
  updateWelcomeStatusAct,
  updateWelcomeStatusErrorAct,
  updateWelcomeStatusSuccessAct,
  assignGroupAct,
  assignGroupErrorAct,
  assignGroupSuccessAct,
  assignCouponAct,
} from './actions';

export const initialState = {
  loadingState: 0,
  error: '',
  team: null,
  setBibState: 0,
  setBibError: '',
  startTimeState: 0,
  tagState: 0,
  statusState: 0,
  userAssign: 0,
  welcomeStatusState: 0,
  groupAssign: 0,
  couponAssign: 0,
};

/* eslint-disable default-case, no-param-reassign */
const eventTeamDetailSlice = createSlice({
  name: 'EventTeamDetail',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getTeamDetail, state => {
        state.loadingState = 1;
        state.error = '';
      })
      .addCase(getTeamDetailError, (state, { payload }) => {
        state.loadingState = 3;
        state.error = payload;
      })
      .addCase(getTeamDetailSuccess, (state, { payload }) => {
        state.loadingState = 2;
        state.error = '';
        state.team = payload;
      })
      .addCase(setTeamBibNumber, state => {
        state.setBibState = 1;
        state.error = '';
      })
      .addCase(setTeamBibNumberError, (state, { payload }) => {
        state.setBibState = 3;
        state.error = payload;
      })
      .addCase(setTeamBibNumberSuccess, (state, { payload }) => {
        state.setBibState = 2;
        state.error = '';
        state.team.bibNumber = payload.bibNumber;
      })
      .addCase(updateStartTime, state => {
        state.startTimeState = 1;
        state.error = '';
      })
      .addCase(updateStartTimeError, (state, { payload }) => {
        state.startTimeState = 3;
        state.error = payload;
      })
      .addCase(updateStartTimeSuccess, (state, { payload }) => {
        state.startTimeState = 2;
        state.error = '';
        state.team.startTime = payload.startTime;
      })
      .addCase(updateTeamStatus, state => {
        state.statusState = 1;
        state.error = '';
      })
      .addCase(updateTeamStatusError, (state, { payload }) => {
        state.statusState = 3;
        state.error = payload;
      })
      .addCase(updateTeamStatusSuccess, (state, { payload }) => {
        state.statusState = 2;
        state.error = '';
        state.team.status = payload.status;
      })
      .addCase(assignUserAct, state => {
        state.userAssign = 1;
      })
      .addCase(assignUserErrorAct, state => {
        state.userAssign = 3;
      })
      .addCase(assignUserSuccessAct, (state, { payload }) => {
        state.team = { ...state.team, ...payload };
        state.userAssign = 2;
      })
      .addCase(updateWelcomeStatusAct, state => {
        state.error = '';
        state.welcomeStatusState = 1;
      })
      .addCase(updateWelcomeStatusErrorAct, (state, { payload }) => {
        state.error = payload;
        state.welcomeStatusState = 3;
      })
      .addCase(updateWelcomeStatusSuccessAct, (state, { payload }) => {
        state.team = { ...state.team, ...payload };
        state.welcomeStatusState = 2;
        state.error = '';
      })
      .addCase(updateTagState, (state, { payload }) => {
        const { data } = payload;
        const { tagId } = data;
        const { team } = state;
        if (team.tags) {
          const tag = team.tags.find(tg => tg.id === tagId);
          if (tag) {
            tag.loadingState = 1;
          }
        }
        state.error = '';
        state.tagState = 1;
      })
      .addCase(updateTagStateError, (state, { payload }) => {
        const { tagId, error, isAssigned } = payload;
        const { team } = state;
        if (team.tags) {
          const tag = team.tags.find(tg => tg.id === tagId);
          if (tag) {
            tag.loadingState = 3;
            tag.status.active = isAssigned;
          }
        }
        state.tagState = 3;
        state.error = error;
      })
      .addCase(updateTagStateSuccess, (state, { payload }) => {
        const { teamTagId: tagId, active } = payload;
        const { team } = state;
        if (team.tags) {
          const tag = team.tags.find(tg => tg.id === tagId);
          if (tag) {
            tag.loadingState = 0;
            tag.status.active = active;
          }
        }
        state.tagState = 2;
        state.error = '';
      })
      .addCase(updateDocumentStatusAct, (state, { payload }) => {
        const { loadingState, memberId, documentId, data } = payload;
        const { team } = state;
        const member = team?.members?.find(mem => mem.id === memberId);
        const document = member?.documents?.find(doc => doc.id === documentId);
        switch (loadingState) {
          case 2:
            // update the document status
            if (document) {
              document.loadingState = 2;
              document.status = data.status;
            }
            break;
          default:
            if (document) {
              document.loadingState = loadingState;
            }
        }
      })
      .addCase(assignGroupAct, (state, { payload }) => {
        state.groupAssign = 1;
        const { metadata } = payload;
        const { currentValue } = metadata;
        state.team = { ...state.team, group: currentValue };
      })
      .addCase(assignGroupErrorAct, (state, { payload }) => {
        const { previousValue } = payload;
        state.team = { ...state.team, group: previousValue };
        state.groupAssign = 3;
      })
      .addCase(assignGroupSuccessAct, (state, { payload }) => {
        state.groupAssign = 2;
        state.team = { ...state.team, ...payload };
      })
      .addCase(assignCouponAct, (state, { payload }) => {
        const { loadingState, metadata } = payload;
        state.couponAssign = loadingState;
        const { currentValue, previousValue } = metadata;
        switch (loadingState) {
          case 1:
            state.team = { ...state.team, coupon: currentValue };
            break;
          case 3:
            state.team = { ...state.team, coupon: previousValue };
            break;
        }
      })
      .addCase(resetGetTeamDetail, () => initialState);
  },
});

export default eventTeamDetailSlice.reducer;
